import { BarChart3, FileText, RefreshCw, ShieldCheck } from "lucide-react";
import { useFlowReveal } from "@/hooks/useFlowReveal";

const standards = [
  { tag: "Legal", label: "RPwD" , title: "Rights of Persons with Disabilities Act" },
  { tag: "Framework", label: "GIGW", title: "Guidelines for Indian Government Websites" },
  { tag: "Standard", label: "IS 17802", title: "Indian Standard 17802" },
];

const sebiCards = [
  { icon: BarChart3, title: "C3 Reporting", desc: "Quarterly accessibility status submission" },
  { icon: FileText, title: "C4 Audit", desc: "Detailed audit with remediation roadmap" },
  { icon: RefreshCw, title: "Annual Re-Assessment", desc: "Ongoing compliance validation" },
];

const IndiaSection = () => {
  const flowRef = useFlowReveal<HTMLDivElement>();

  return (
  <section id="india" className="section-padding section-alt" aria-labelledby="india-heading">
    <div className="max-w-7xl mx-auto">
      <p className="eyebrow mb-4">India</p>
      <h2 id="india-heading" className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-3xl">
        Accessibility Audit India — <abbr title="Web Content Accessibility Guidelines">WCAG</abbr> Compliance India
      </h2>

      {/* Standard tags */}
      <div className="flex flex-wrap gap-3 mb-6" role="list" aria-label="Indian accessibility standards" data-tts-skip>
        {standards.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-2 border bg-card text-primary text-xs font-semibold px-3 py-2 rounded-md min-h-[44px]" role="listitem">
            <abbr title={s.title}>{s.label}</abbr> <span className="text-muted-foreground">– {s.tag}</span>
          </span>
        ))}
      </div>

      <p className="text-muted-foreground max-w-3xl mb-10 text-base leading-relaxed">
        As a leading accessibility testing company in India, we deliver <abbr title="Web Content Accessibility Guidelines">WCAG</abbr> audit services covering <abbr title="Rights of Persons with Disabilities Act">RPwD</abbr> Act 2016, <abbr title="Guidelines for Indian Government Websites">GIGW</abbr> 3.0, <abbr title="Indian Standard 17802">IS 17802</abbr>, and <abbr title="Securities and Exchange Board of India">SEBI</abbr> guidelines. Our accessibility compliance software ensures website accessibility compliance aligned with global benchmarks.
      </p>

      {/* SEBI Alignment */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={20} className="text-primary" aria-hidden="true" />
          <h3 className="font-display text-xl font-bold text-foreground"><abbr title="Securities and Exchange Board of India">SEBI</abbr> Alignment</h3>
        </div>
        <p className="text-base text-muted-foreground mb-2">Securities and Exchange Board of India Circular Compliance</p>
        <p className="text-sm text-muted-foreground mb-6">Accessibility compliance for listed entities</p>

        <div ref={flowRef} className="grid sm:grid-cols-3 gap-6">
          {sebiCards.map((c, i) => (
            <article key={c.title} className="flow-step border-t-2 border-foreground pt-5" style={{ "--i": Math.min(i, 5) } as React.CSSProperties}>
              <div className="mb-4" aria-hidden="true">
                <c.icon size={20} className="text-primary" />
              </div>
              <h4 className="font-display font-bold text-foreground mb-1">{c.title}</h4>
              <p className="text-base text-muted-foreground">{c.desc}</p>
            </article>
          ))}
        </div>
      </div>

      {/* The three SEBI steps above are the compliance journey; this is the note that used to repeat them. */}
      <p className="max-w-3xl border-l-2 border-primary pl-5 text-base leading-relaxed text-muted-foreground">
        Our approach covers the whole regulatory journey, from C3 reporting through the annual review.
        Reports are version-controlled after submission, with change tracking from one cycle to the next,
        so progress on remediation and audit readiness stays visible.
      </p>
    </div>
  </section>
  );
};

export default IndiaSection;
